import { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { MoreHoriz, NavArrowRight } from 'iconoir-react';
import { MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  dropdownMenuItemDestructiveClass,
} from 'components/ui/dropdown-menu';
import { cn } from 'lib/utils';
import { useIsCompactSidebar } from 'hooks/use-mobile';
import { modalOption } from '/models/sidebar';
import { ConversationMenuIcon } from './conversation-menu-icon';

const destructiveOptionIds = new Set(['delete', 'leave']);

const SIDEBAR_MENU_WIDTH = 250;
const SIDEBAR_MENU_GAP = 4;
const SIDEBAR_MENU_MARGIN = 8;

interface ConversationContextMenuProps {
  options: modalOption[];
  contentSide?: 'top' | 'right' | 'bottom' | 'left';
  contentAlign?: 'start' | 'center' | 'end';
  variant?: 'sidebar' | 'page';
  triggerClassName?: string;
  triggerAriaLabel?: string;
}

export function getSidebarConversationMenuPosition(
  trigger: Pick<DOMRect, 'right' | 'bottom' | 'top'>,
  menu: { width: number; height: number },
  viewport: { width: number; height: number },
  preferBelow = false,
) {
  let left = trigger.right + SIDEBAR_MENU_GAP;
  const maxLeft = viewport.width - menu.width - SIDEBAR_MENU_MARGIN;
  if (left > maxLeft) {
    left = Math.max(SIDEBAR_MENU_MARGIN, maxLeft);
  }

  if (preferBelow) {
    let top = trigger.bottom + SIDEBAR_MENU_GAP;
    const overflowBottom = top + menu.height > viewport.height - SIDEBAR_MENU_MARGIN;
    if (overflowBottom) {
      top = Math.max(SIDEBAR_MENU_MARGIN, trigger.top - menu.height - SIDEBAR_MENU_GAP);
    }
    return { top, left };
  }

  let top = trigger.bottom - menu.height;
  const maxTop = viewport.height - menu.height - SIDEBAR_MENU_MARGIN;
  if (top > maxTop) {
    top = Math.max(SIDEBAR_MENU_MARGIN, maxTop);
  }
  if (top < SIDEBAR_MENU_MARGIN) {
    top = SIDEBAR_MENU_MARGIN;
  }

  return { top, left };
}

export function getSidebarConversationSubmenuPosition(
  trigger: Pick<DOMRect, 'left' | 'right' | 'top'>,
  menu: { width: number; height: number },
  viewport: { width: number; height: number },
) {
  const rightCandidate = trigger.right + SIDEBAR_MENU_GAP;
  const leftCandidate = trigger.left - menu.width - SIDEBAR_MENU_GAP;
  const maxLeft = viewport.width - menu.width - SIDEBAR_MENU_MARGIN;
  const fitsRight = rightCandidate <= maxLeft;

  let left = fitsRight ? rightCandidate : leftCandidate;
  if (left < SIDEBAR_MENU_MARGIN) {
    left = SIDEBAR_MENU_MARGIN;
  }
  if (left > maxLeft) {
    left = Math.max(SIDEBAR_MENU_MARGIN, maxLeft);
  }

  let top = trigger.top;
  const maxTop = viewport.height - menu.height - SIDEBAR_MENU_MARGIN;
  if (top > maxTop) {
    top = Math.max(SIDEBAR_MENU_MARGIN, maxTop);
  }
  if (top < SIDEBAR_MENU_MARGIN) {
    top = SIDEBAR_MENU_MARGIN;
  }

  return { top, left };
}

export function shouldIgnoreSidebarSubmenuClose(nextOpen: boolean, openedByTouch: boolean) {
  return !nextOpen && openedByTouch;
}

function applySidebarFloatingPanelStyles(menuNode: HTMLElement, top: number, left: number) {
  const wrapper = menuNode.parentElement;
  if (!wrapper) return;

  wrapper.style.setProperty('position', 'fixed', 'important');
  wrapper.style.setProperty('left', `${left}px`, 'important');
  wrapper.style.setProperty('top', `${top}px`, 'important');
  wrapper.style.setProperty('right', 'auto', 'important');
  wrapper.style.setProperty('bottom', 'auto', 'important');
  wrapper.style.setProperty('transform', 'none', 'important');
  wrapper.style.setProperty('max-width', 'none', 'important');
  wrapper.style.setProperty('min-width', `${SIDEBAR_MENU_WIDTH}px`, 'important');
  wrapper.style.setProperty('width', `${SIDEBAR_MENU_WIDTH}px`, 'important');
  wrapper.style.setProperty('overflow', 'visible', 'important');
  wrapper.style.setProperty('z-index', '200', 'important');

  menuNode.style.setProperty('width', `${SIDEBAR_MENU_WIDTH}px`, 'important');
  menuNode.style.setProperty('min-width', `${SIDEBAR_MENU_WIDTH}px`, 'important');
  menuNode.style.setProperty('max-width', 'none', 'important');
  menuNode.style.setProperty('overflow', 'visible', 'important');
}

function pinSidebarMenuToTrigger(
  menuNode: HTMLElement,
  triggerNode: HTMLElement,
  preferBelow = false,
) {
  const wrapper = menuNode.parentElement;
  if (!wrapper) return;

  const menuHeight = Math.max(menuNode.offsetHeight, wrapper.offsetHeight, 1);
  const { top, left } = getSidebarConversationMenuPosition(
    triggerNode.getBoundingClientRect(),
    { width: SIDEBAR_MENU_WIDTH, height: menuHeight },
    { width: window.innerWidth, height: window.innerHeight },
    preferBelow,
  );

  applySidebarFloatingPanelStyles(menuNode, top, left);
}

function pinSidebarSubmenuToTrigger(menuNode: HTMLElement, triggerNode: HTMLElement) {
  const wrapper = menuNode.parentElement;
  if (!wrapper) return;

  const menuHeight = Math.max(menuNode.offsetHeight, wrapper.offsetHeight, 1);
  const { top, left } = getSidebarConversationSubmenuPosition(
    triggerNode.getBoundingClientRect(),
    { width: SIDEBAR_MENU_WIDTH, height: menuHeight },
    { width: window.innerWidth, height: window.innerHeight },
  );

  applySidebarFloatingPanelStyles(menuNode, top, left);
}

export function ConversationContextMenu({
  options,
  contentSide = 'right',
  contentAlign = 'end',
  variant = 'sidebar',
  triggerClassName,
  triggerAriaLabel,
}: ConversationContextMenuProps) {
  const isCompact = useIsCompactSidebar();
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [expandedSubmenuId, setExpandedSubmenuId] = useState<string | null>(null);
  const firstDestructiveIndex = options.findIndex((o) => destructiveOptionIds.has(o.id));

  const isPageVariant = variant === 'page';
  const isSidebarVariant = variant === 'sidebar';
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLElement | null>(null);
  const subTriggerRef = useRef<HTMLDivElement>(null);
  const subMenuRef = useRef<HTMLElement | null>(null);
  const openedByTouchRef = useRef(false);
  const ignoreSubmenuCloseUntilRef = useRef(0);

  const handleRootOpenChange = useCallback((next: boolean) => {
    if (!next) {
      openedByTouchRef.current = false;
      setSubOpen(false);
      setExpandedSubmenuId(null);
    }
    setOpen(next);
  }, []);

  const handleSubOpenChange = useCallback((next: boolean) => {
    if (shouldIgnoreSidebarSubmenuClose(next, openedByTouchRef.current)) return;
    if (!next) openedByTouchRef.current = false;
    setSubOpen(next);
  }, []);

  const pinSidebarMenu = useCallback(() => {
    if (!menuRef.current || !triggerRef.current) return;
    pinSidebarMenuToTrigger(menuRef.current, triggerRef.current, isCompact);
  }, [isCompact]);

  const pinSidebarSubmenu = useCallback(() => {
    if (!subMenuRef.current || !subTriggerRef.current) return;
    pinSidebarSubmenuToTrigger(subMenuRef.current, subTriggerRef.current);
  }, []);

  useLayoutEffect(() => {
    if (!open || !isSidebarVariant) return;
    const pin = () => {
      pinSidebarMenu();
      pinSidebarSubmenu();
    };
    pin();
    const frame = requestAnimationFrame(pin);
    window.addEventListener('resize', pin);
    window.addEventListener('scroll', pin, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', pin);
      window.removeEventListener('scroll', pin, true);
    };
  }, [open, subOpen, isSidebarVariant, pinSidebarMenu, pinSidebarSubmenu, expandedSubmenuId]);

  const menuItems = options.map((option, index) => {
    const isDestructive = destructiveOptionIds.has(option.id);
    const showSeparator =
      isDestructive && index === firstDestructiveIndex && firstDestructiveIndex > 0;

    if (option.type === 'submenu') {
      if (isCompact) {
        const isExpanded = expandedSubmenuId === option.id;
        return (
          <Fragment key={option.id}>
            <DropdownMenuItem
              disabled={option.isDisabled}
              onSelect={(event) => {
                event.preventDefault();
                setExpandedSubmenuId(isExpanded ? null : option.id);
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <ConversationMenuIcon optionId={option.id} />
              <span>{option.name}</span>
              <NavArrowRight
                width={16}
                height={16}
                strokeWidth={1.5}
                className={cn('ml-auto size-4 shrink-0', isExpanded && 'rotate-90')}
              />
            </DropdownMenuItem>
            {isExpanded &&
              option.subOptions?.map((subOption) => (
                <DropdownMenuItem
                  key={subOption.id}
                  className="pl-8"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!subOption?.isDisabled && subOption?.callback) {
                      subOption.callback();
                    }
                  }}
                  disabled={subOption?.isDisabled}
                >
                  <ConversationMenuIcon optionId={subOption.id} />
                  <span>{subOption.name}</span>
                </DropdownMenuItem>
              ))}
          </Fragment>
        );
      }

      return (
        <DropdownMenuSub
          key={option.id}
          {...(isSidebarVariant ? { open: subOpen, onOpenChange: handleSubOpenChange } : {})}
        >
          <DropdownMenuSubTrigger
            ref={isSidebarVariant ? subTriggerRef : undefined}
            onPointerDown={(e) => {
              e.stopPropagation();
              if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                openedByTouchRef.current = true;
                ignoreSubmenuCloseUntilRef.current = Date.now() + 700;
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (
                isSidebarVariant &&
                openedByTouchRef.current &&
                subOpen &&
                Date.now() >= ignoreSubmenuCloseUntilRef.current
              ) {
                openedByTouchRef.current = false;
                setSubOpen(false);
              }
            }}
            disabled={option.isDisabled}
          >
            <ConversationMenuIcon optionId={option.id} />
            <span>{option.name}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent
            avoidCollisions={!isSidebarVariant}
            {...(isSidebarVariant
              ? {
                  container: document.body,
                  sideOffset: 4,
                  'data-conversation-sidebar-submenu': '',
                  className: 'z-[200] w-[250px] min-w-[250px] max-w-[250px] overflow-visible',
                  style: { width: 250, minWidth: 250, maxWidth: 250 },
                  onFocusOutside: (event: Event) => {
                    if (openedByTouchRef.current) event.preventDefault();
                  },
                  onPointerDownOutside: (event: Event) => {
                    if (openedByTouchRef.current && Date.now() < ignoreSubmenuCloseUntilRef.current) {
                      event.preventDefault();
                    }
                  },
                  ref: (node: HTMLDivElement | null) => {
                    subMenuRef.current = node;
                    if (node && subTriggerRef.current) {
                      pinSidebarSubmenuToTrigger(node, subTriggerRef.current);
                    }
                  },
                }
              : {})}
          >
            {option.subOptions?.map((subOption) => (
              <DropdownMenuItem
                key={subOption.id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!subOption?.isDisabled && subOption?.callback) {
                    subOption.callback();
                  }
                }}
                disabled={subOption?.isDisabled}
              >
                <ConversationMenuIcon optionId={subOption.id} />
                <span>{subOption.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      );
    }

    return (
      <Fragment key={option.id}>
        {showSeparator && <DropdownMenuSeparator />}
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            option.callback?.();
          }}
          disabled={option.isDisabled}
          className={cn(isDestructive && dropdownMenuItemDestructiveClass)}
        >
          <ConversationMenuIcon optionId={option.id} />
          <span>{option.name}</span>
        </DropdownMenuItem>
      </Fragment>
    );
  });

  return (
    <DropdownMenu modal={!isSidebarVariant} open={open} onOpenChange={handleRootOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            isPageVariant
              ? 'relative size-6 shrink-0 text-[#4f4f4f] hover:opacity-80 dark:text-foreground'
              : 'ml-1 rounded-md p-1 hover:bg-muted-foreground/10',
            triggerClassName
          )}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          aria-label={triggerAriaLabel}
        >
          {isPageVariant ? (
            <MoreVertical className="size-6" strokeWidth={1.75} />
          ) : (
            <MoreHoriz
              width={18}
              height={18}
              strokeWidth={1.5}
              className="text-sidebar-foreground group-hover:text-[#3D3D3D] dark:group-hover:!text-[#fff]"
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={isSidebarVariant ? 'right' : contentSide}
        align={isSidebarVariant ? 'end' : contentAlign}
        sideOffset={4}
        avoidCollisions={!isSidebarVariant}
        container={isSidebarVariant ? document.body : undefined}
        data-conversation-sidebar-menu={isSidebarVariant ? '' : undefined}
        className={
          isSidebarVariant ? 'z-[200] w-[250px] min-w-[250px] max-w-[250px] overflow-visible' : undefined
        }
        style={isSidebarVariant ? { width: 250, minWidth: 250, maxWidth: 250 } : undefined}
        ref={
          isSidebarVariant
            ? (node) => {
                menuRef.current = node;
                if (node && triggerRef.current) {
                  pinSidebarMenuToTrigger(node, triggerRef.current, isCompact);
                }
              }
            : undefined
        }
      >
        {menuItems}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
